import { useState, useRef, useId, type DragEvent } from 'react';
import { Dialog } from '@/components/common/Dialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import {
  Upload,
  FileArchive,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  SkipForward,
} from 'lucide-react';
import { getAuthToken, getApiBaseUrl } from '@/api/client';
import { reportError } from '@shared/error-reporter';

type Mode = 'upload' | 'progress';

interface ImportErrorDetail {
  file: string;
  message: string;
}

interface ImportSummary {
  imported: number;
  skipped: number;
  errors: number;
  errorDetails?: ImportErrorDetail[];
}

export function UploadPage() {
  const { showToast } = useToast();
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('upload');
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [zipFiles, setZipFiles] = useState<File[]>([]);
  const [useCurrentSetup, setUseCurrentSetup] = useState(true);
  const checkboxId = useId();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addFiles = async (files: FileList | File[]) => {
    const validFiles = Array.from(files).filter(f => {
      const lower = f.name.toLowerCase();
      return lower.endsWith('.zip') || lower.endsWith('.xrk') || lower.endsWith('.xrz');
    });
    if (validFiles.length === 0) {
      showToast(t('import.onlySupportedAccepted'), 'error');
      return;
    }

    const buffered = await Promise.all(
      validFiles.map(async (f) => {
        try {
          const buf = await f.arrayBuffer();
          const blob = new File([buf], f.name, { type: f.type || 'application/octet-stream' });
          return blob;
        } catch {
          return f;
        }
      })
    );

    setZipFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const novel = buffered.filter(f => !existing.has(f.name + f.size));
      return [...prev, ...novel];
    });
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setZipFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setZipFiles([]);
    setMode('upload');
    setProgress(0);
    setStatus('');
    setSummary(null);
    setShowSummaryDialog(false);
  };

  const startZipUpload = async () => {
    if (zipFiles.length === 0) return;
    setMode('progress');
    setStatus(t('import.uploading'));
    setProgress(0);

    const formData = new FormData();
    zipFiles.forEach(f => formData.append('files', f));

    try {
      const xhr = new XMLHttpRequest();
      const token = getAuthToken();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setProgress(pct);
          setStatus(t('import.uploadingPct', { n: pct }));
        }
      });

      const result = await new Promise<ImportSummary>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve({
                imported: data.imported ?? 0,
                skipped: data.skipped ?? 0,
                errors: data.errors ?? 0,
                errorDetails: data.errorDetails ?? [],
              });
            } catch {
              resolve({ imported: 0, skipped: 0, errors: 0 });
            }
          } else {
            try {
              const data = JSON.parse(xhr.responseText);
              reject(new Error(data.error || t('import.uploadFailed', { status: String(xhr.status) })));
            } catch {
              reject(new Error(t('import.uploadFailed', { status: String(xhr.status) })));
            }
          }
        });

        xhr.addEventListener('error', () => reject(new Error(t('import.connectionLost'))));
        xhr.addEventListener('abort', () => reject(new Error(t('import.uploadAborted'))));

        xhr.open('POST', `${getApiBaseUrl()}/rpc/ImportZips?useSetup=${useCurrentSetup}`);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      setSummary(result);
      setShowSummaryDialog(true);
      if (result.imported > 0) {
        showToast(t('import.importedFromZip', { n: result.imported }));
        window.dispatchEvent(new CustomEvent('queue:updated'));
      }
    } catch (e) {
      const err = e as Error;
      reportError(new Error(`ImportZips failed: ${err.message}`));
      setStatus(t('import.importFailed'));
      const failureSummary = { imported: 0, skipped: 0, errors: 1 };
      setSummary(failureSummary);
      setShowSummaryDialog(true);
      showToast(t('import.zipImportFailed', { error: err.message }), 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Upload size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('settings.uploadSessions')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('import.uploadZipDesc')}</p>
        </div>
      </div>

      {/* Main Upload Card */}
      <div className="card p-6 space-y-6">
        {mode === 'upload' && (
          <div className="space-y-6">
            <div
              className={`drop-zone ${isDragging ? 'drop-zone--active' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInput}
                className="hidden"
                multiple
                accept=".zip,.xrk,.xrz,application/zip,application/x-zip-compressed,application/octet-stream"
              />
              <FileArchive size={32} className="text-accent mb-3 opacity-80" />
              <div className="font-medium text-sm text-text-primary mb-1">
                {t('import.dragDropZip')}
              </div>
              <div className="text-xs text-text-muted">
                {t('import.orBrowse')} <span className="text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent transition-all">{t('import.browseFiles')}</span>
              </div>
            </div>

            {zipFiles.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold tracking-wide text-text-muted uppercase mb-2">
                  {t('import.filesSelected', { n: zipFiles.length })}
                </h4>
                <div className="space-y-1">
                  {zipFiles.map((file, i) => (
                    <div key={`${file.name}-${i}`} className="flex items-center justify-between p-2 rounded-md bg-bg-raised border border-border-subtle group text-sm">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileArchive size={14} className="text-text-muted shrink-0" />
                        <span className="truncate text-text-primary font-mono text-xs">{file.name}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                        className="p-1 rounded text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label
              htmlFor={checkboxId}
              className="flex items-start gap-3 p-3 rounded-md bg-bg-raised border border-border-subtle hover:border-border-default transition-colors cursor-pointer select-none"
            >
              <input
                type="checkbox"
                id={checkboxId}
                checked={useCurrentSetup}
                onChange={(e) => setUseCurrentSetup(e.target.checked)}
                className="mt-0.5 accent-accent w-4 h-4 cursor-pointer shrink-0"
              />
              <div>
                <div className="text-sm font-medium text-text-primary leading-tight">
                  {t('import.useCurrentSetup')}
                </div>
                <div className="text-xs text-text-muted mt-0.5 leading-relaxed">
                  {t('import.useCurrentSetupDesc')}
                </div>
              </div>
            </label>

            <div className="flex justify-end pt-2">
              <button
                onClick={startZipUpload}
                disabled={zipFiles.length === 0}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer ${
                  zipFiles.length > 0
                    ? 'bg-accent text-white hover:bg-accent/90'
                    : 'bg-bg-raised text-text-muted cursor-not-allowed'
                }`}
              >
                <Upload size={14} />
                {t('import.upload')} {zipFiles.length > 0 ? `(${zipFiles.length})` : ''}
              </button>
            </div>
          </div>
        )}

        {mode === 'progress' && (
          <div className="py-12 text-center space-y-6">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto text-accent animate-pulse">
              <Upload size={24} className="animate-bounce" />
            </div>
            <div>
              <div className="text-base font-semibold text-text-primary mb-1">{status}</div>
            </div>

            <div className="w-full max-w-sm mx-auto h-2 bg-bg-raised border border-border-subtle rounded-full overflow-hidden">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Dialog */}
      {showSummaryDialog && summary && (
        <Dialog
          open
          onClose={resetForm}
          title={t('import.title')}
          footer={(
            <button
              onClick={resetForm}
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-all cursor-pointer ml-auto"
            >
              {t('common.close')}
            </button>
          )}
        >
          <div className="space-y-6">
            {summary.imported > 0 ? (
              <div className="p-4 rounded-md bg-green-500/10 border border-green-500/20 flex gap-3 text-green-500">
                <CheckCircle2 className="shrink-0" size={20} />
                <div className="text-sm">
                  <div className="font-medium mb-0.5">{t('import.successMessage')}</div>
                </div>
              </div>
            ) : summary.errors === 0 && summary.skipped > 0 ? (
              <div className="p-4 rounded-md bg-blue-500/10 border border-blue-500/20 flex gap-3 text-blue-400">
                <SkipForward className="shrink-0" size={20} />
                <div className="text-sm">
                  <div className="font-medium mb-0.5">{t('import.noNewSessions')}</div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
                <div className="text-2xl font-bold text-green-500 mb-1">{summary.imported}</div>
                <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.imported')}</div>
              </div>
              <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
                <div className="text-2xl font-bold text-text-secondary mb-1">{summary.skipped}</div>
                <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.skipped')}</div>
              </div>
              <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
                <div className={`text-2xl font-bold mb-1 ${summary.errors > 0 ? 'text-red-500' : 'text-text-secondary'}`}>
                  {summary.errors}
                </div>
                <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.errors')}</div>
              </div>
            </div>

            {summary.errorDetails && summary.errorDetails.length > 0 && (
              <div className="p-4 rounded-md bg-red-500/10 border border-red-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-red-400 shrink-0" />
                  <span className="text-sm font-medium text-red-400">{t('import.errorDetailsTitle')}</span>
                </div>
                <div className="space-y-1">
                  {summary.errorDetails.map((detail, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="font-mono text-text-secondary truncate shrink-0 max-w-[40%]">{detail.file}</span>
                      <span className="text-red-300">{detail.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
